import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase-client';
import { useAuth } from '../../auth/AuthContext';
import { withModuleDefaults } from '../teams/utils/moduleUtils';
import { useOnlineRefreshSignal } from '../../utils/onlineStatus';
function toSortedByName(items) {
  return [...items].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, {
    sensitivity: 'base'
  }));
}
export default function CalendarSourceSwitcher({
  currentScope = 'user',
  currentFamilyId = '',
  currentFamilyName = '',
  compact = false
}) {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const [teamCalendars, setTeamCalendars] = useState([]);
  const [familyCalendars, setFamilyCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const onlineRefreshSignal = useOnlineRefreshSignal();
  useEffect(() => {
    if (!user?.uid) {
      setTeamCalendars([]);
      setFamilyCalendars([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const loadSources = async () => {
      setLoading(true);
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (!userSnap.exists()) {
          if (!cancelled) {
            setTeamCalendars([]);
            setFamilyCalendars([]);
          }
          return;
        }
        const userData = userSnap.data() || {};
        const teamIds = Object.keys(userData.team_roles || {});
        const familyIds = Object.keys(userData.family_roles || {});
        const [teamDocs, familyDocs] = await Promise.all([Promise.all(teamIds.map(teamId => getDoc(doc(db, 'teams', teamId)))), Promise.all(familyIds.map(familyId => getDoc(doc(db, 'families', familyId))))]);
        const nextTeamCalendars = teamDocs.map((snap, idx) => ({
          snap,
          teamId: teamIds[idx]
        })).filter(({
          snap
        }) => snap.exists()).map(({
          snap,
          teamId
        }) => {
          const data = snap.data() || {};
          return {
            id: teamId,
            name: data.name || 'Club',
            eventsEnabled: withModuleDefaults(data.modules).events?.enabled
          };
        }).filter(team => team.eventsEnabled);
        const nextFamilyCalendars = familyDocs.map((snap, idx) => ({
          snap,
          familyId: familyIds[idx]
        })).filter(({
          snap
        }) => snap.exists()).map(({
          snap,
          familyId
        }) => ({
          id: familyId,
          name: snap.data()?.name || 'Family'
        }));
        if (!cancelled) {
          setTeamCalendars(toSortedByName(nextTeamCalendars));
          setFamilyCalendars(toSortedByName(nextFamilyCalendars));
        }
      } catch (err) {
        // Retain the last successful sources during a transient refresh failure.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadSources();
    return () => {
      cancelled = true;
    };
  }, [onlineRefreshSignal, user?.uid]);
  const activeFamilyName = useMemo(() => {
    if (!currentFamilyId) return '';
    if (currentFamilyName) return currentFamilyName;
    const matched = familyCalendars.find(family => family.id === currentFamilyId);
    return matched?.name || 'Family';
  }, [currentFamilyId, currentFamilyName, familyCalendars]);
  const viewingLabel = currentScope === 'family' ? `You are viewing: ${activeFamilyName || 'Family'} calendar` : 'You are viewing: your personal calendar';
  const currentRoute = currentScope === 'family' && currentFamilyId ? `/calendar/family/${currentFamilyId}` : '/calendar/user';
  const familyOptions = useMemo(() => {
    const base = [...familyCalendars];
    if (currentScope === 'family' && currentFamilyId && !base.some(family => family.id === currentFamilyId)) {
      base.unshift({
        id: currentFamilyId,
        name: activeFamilyName || 'Family'
      });
    }
    return base;
  }, [familyCalendars, currentScope, currentFamilyId, activeFamilyName]);
  const hasAlternativeCalendars = familyOptions.length > 0 || teamCalendars.length > 0;
  if (compact) {
    return <select value={currentRoute} disabled={loading} aria-label="Switch calendar view" onChange={event => {
      const nextRoute = event.target.value;
      if (nextRoute && nextRoute !== currentRoute) {
        navigate(nextRoute);
      }
    }} style={{
      border: '1px solid #cbd5e1',
      borderRadius: 8,
      padding: '0.42rem 0.6rem',
      fontSize: 14,
      lineHeight: 1.2,
      background: '#fff',
      color: '#0f172a',
      minWidth: 220
    }} title={loading ? 'Loading calendars...' : 'Switch calendar view'}>
        <option value="/calendar/user">Personal calendar</option>

        {familyOptions.length > 0 && <optgroup label="Family calendars">
            {familyOptions.map(family => <option key={family.id} value={`/calendar/family/${family.id}`}>
                {family.name}
              </option>)}
          </optgroup>}

        {teamCalendars.length > 0 && <optgroup label="Club calendars">
            {teamCalendars.map(team => <option key={team.id} value={`/teams/${team.id}/calendar`}>
                {team.name}
              </option>)}
          </optgroup>}
      </select>;
  }
  return <div className="card" style={{
    marginBottom: 12
  }}>
      <div className="card-body" style={{
      display: 'grid',
      gap: 8
    }}>
        <div>
          <strong>{viewingLabel}.</strong>{' '}
          These pages show committed events only (RSVP "Going" or paid tickets).
        </div>

        <div style={{
        display: 'grid',
        gap: 6
      }}>
          <label htmlFor="calendar-source-combobox" style={{
          fontWeight: 600
        }}>
            Switch calendar view:
          </label>
          <div style={{
          display: 'grid',
          gap: 6,
          maxWidth: 420
        }}>
            <select id="calendar-source-combobox" value={currentRoute} disabled={loading} onChange={event => {
            const nextRoute = event.target.value;
            if (nextRoute && nextRoute !== currentRoute) {
              navigate(nextRoute);
            }
          }} style={{
            border: '1px solid #cbd5e1',
            borderRadius: 8,
            padding: '0.5rem 0.65rem',
            fontSize: 14,
            background: '#fff',
            color: '#0f172a'
          }}>
              <option value="/calendar/user">Personal calendar</option>

              {familyOptions.length > 0 && <optgroup label="Family calendars">
                  {familyOptions.map(family => <option key={family.id} value={`/calendar/family/${family.id}`}>
                      {family.name}
                    </option>)}
                </optgroup>}

              {teamCalendars.length > 0 && <optgroup label="Club calendars">
                  {teamCalendars.map(team => <option key={team.id} value={`/teams/${team.id}/calendar`}>
                      {team.name}
                    </option>)}
                </optgroup>}
            </select>
            {loading && <span style={{
            color: 'var(--clr-text-muted)'
          }}>Loading calendars...</span>}
            {!loading && !hasAlternativeCalendars && <span style={{
            color: 'var(--clr-text-muted)'
          }}>
                No family or club calendars available.
              </span>}
          </div>
        </div>
      </div>
    </div>;
}
